import { Inject, Injectable, PLATFORM_ID, DOCUMENT } from '@angular/core';
import { LocalStorageService } from 'ngx-webstorage';
import { isPlatformBrowser } from '@angular/common';
import {
  LANGUAGES,
  parseLanguageCookie,
  serializeLanguageCookie,
  type LanguageCode,
} from '@handyhand/i18n';

/**
 * Stored language — the `LANGUAGES` tuple element preserves `code` as the
 * narrowed `LanguageCode` literal type rather than the widened `string` from
 * the `Language` interface.
 */
export type StoredLanguage = (typeof LANGUAGES)[number];

/**
 * Legacy storage shape — header dropdown used to save
 * `{ name, value, flag, flagImg }` with `value` as the old language code
 * ('dk' | 'en' | 'nl'). New shape stores the full `Language` from
 * `@handyhand/i18n`. Returns null when the legacy value can't be mapped.
 */
function migrateLegacyLang(stored: unknown): StoredLanguage | null {
  if (!stored || typeof stored !== 'object') return null;
  const obj = stored as Record<string, unknown>;
  // Already migrated.
  if (typeof obj['code'] === 'string') {
    return LANGUAGES.find((l) => l.code === obj['code']) ?? null;
  }
  const legacyValue = obj['value'];
  if (typeof legacyValue !== 'string') return null;
  // 'dk' was a country-coded language identifier — map to ISO 639-1 'da'.
  const code = legacyValue === 'dk' ? 'da' : (legacyValue as LanguageCode);
  return LANGUAGES.find((l) => l.code === code) ?? null;
}

const keys = {
  adsClickObj: 'adsClickObj',
  allQueryParams: 'allQueryParams',
  authToken: 'pinploy_authToken',
  completeprofileviewed: 'pinploy_completeprofileviewed',
  cookie_consent: 'cookie_consent',
  createComment: 'pinploy_createComment',
  createTask: 'pinploy_createTask',
  filter: 'pinploy_filter',
  lang: 'pinploy_lang',
  notificationSettings: 'notification_settings',
  prompt_task_owners_once: 'prompt_task_owners_once',
  prompt_handyhanders_once: 'prompt_handyhanders_once',
  settingsTab: 'pinploy_settingsTab',
  socketId: 'pinploy_socketId',
  tags: 'pinploy_tags',
  task: 'pinploy_task',
  user: 'pinploy_user',
  userSettings: 'user_settings',
};

@Injectable({ providedIn: 'root' })
export class StorageService {
  private isBrowser: boolean;
  constructor(
    private localStorage: LocalStorageService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  clear() {
    this.localStorage.clear();
  }

  get authToken(): any {
    return this.get(keys.authToken);
  }
  set authToken(authToken: any) {
    this.save(keys.authToken, authToken);
  }

  getCookie(name: string) {
    if (!this.isBrowser) return;
    let cookieArr = this.document.cookie.split(';');
    for (let i = 0; i < cookieArr.length; i++) {
      let cookiePair = cookieArr[i].split('=');
      if (name == cookiePair[0].trim()) {
        return decodeURIComponent(cookiePair[1]);
      }
    }
    return null;
  }

  setCookie = (name: any, value: any) => {
    var d = new Date();
    d.setTime(d.getTime() + 24 * 60 * 60 * 1000 * 400);
    this.document.cookie =
      name + '=' + value + ';path=/;expires=' + d.toUTCString();
  };

  public save(key: string, object: any): void {
    if (!this.isBrowser) return;
    this.localStorage.store(key, object);
  }
  public get(key: string): any {
    if (!this.isBrowser) return;
    return this.localStorage.retrieve(key);
  }

  get user(): any {
    if (!this.isBrowser) return;
    return this.get(keys.user);
  }
  set user(user: any) {
    if (!this.isBrowser) return;
    delete user.password;
    this.save(keys.user, user);
  }

  get cookie_consent(): boolean {
    if (!this.isBrowser) return false;
    return this.get(keys.cookie_consent);
  }

  set cookie_consent(cookie_consent: boolean) {
    if (!this.isBrowser) return;
    this.save(keys.cookie_consent, cookie_consent);
  }

  get adsClickObj(): any {
    if (!this.isBrowser) return;
    return this.get(keys.adsClickObj);
  }
  set adsClickObj(value) {
    if (!this.isBrowser) return;
    this.save(keys.adsClickObj, value);
  }

  get allQueryParams(): Record<string, string> | null {
    if (!this.isBrowser) return null;
    return this.get(keys.allQueryParams);
  }
  set allQueryParams(value: Record<string, string> | null) {
    if (!this.isBrowser) return;
    this.save(keys.allQueryParams, value);
  }

  /** One-time: seed the shared language cookie from the legacy localStorage
   *  value, then the legacy key is unused. No-ops once a cookie exists. */
  migrateLegacyLanguageToCookie(): void {
    if (!this.isBrowser || this.languageCookie) {
      return;
    }
    const migrated = migrateLegacyLang(this.get(keys.lang));
    if (migrated) {
      this.languageCookie = migrated.code;
    }
  }

  /** Canonical cross-app language preference, stored in the shared cookie. */
  get languageCookie(): LanguageCode | null {
    if (!this.isBrowser) return null;
    return parseLanguageCookie(this.document.cookie);
  }
  set languageCookie(code: LanguageCode | null) {
    if (!this.isBrowser || !code) return;
    const secure = this.document.location?.protocol === 'https:';
    this.document.cookie = serializeLanguageCookie(code, { secure });
  }
}
